import { useAuth } from "@clerk/clerk-react";
import { AlertContext } from "contexts/Alert";
import { BentoCategoriesContext } from "contexts/BentoCategories";
import { BrandsContext, TagLabel } from "contexts/Brands";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { UserRequestsContext } from "contexts/UserRequests";
import lodash from "lodash";
import { Dispatch, SetStateAction, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import {
  BentoBrandMetadataTags,
  BentoBrandSearchParameters,
  CombinedRequest,
  DISCOVER_FILTER,
  FollowingSizeRange,
  LOCATIONS,
  MetadataLocationType,
  MetadataType,
  RequestStatus,
  SavedBrandStatus,
  SearchParams,
  TargetAgeRange,
  UserRequestSource,
} from "schemas/dashboard";
import { ALL } from "schemas/forms";
import { CustomEvent, Map } from "schemas/functions";

import { DiscoverSearchGeopoint } from "components/LocationAutocomplete/schema";

import { fetcherAuth } from "./api";
import { DISCOVER_METADATA_TAGS, DISCOVER_SEARCH_PARAMS } from "./localStorage";
import { trackEvent } from "./tracking";

export const MetadataTypeToParams: Map = {
  [MetadataType.tags]: SearchParams.TAGS_DISCOVER,
};

export const useSearchBrand = () => {
  const { decreaseBrandRequestsCount } = useContext(SubscriptionContext);
  const { setUserRequests, setTotal } = useContext(UserRequestsContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    setSelectedMetadataTags,
    discoverFilterParams,
    selectedMetadataTags,
  } = useContext(BrandsContext);
  const { categoriesById } = useContext(BentoCategoriesContext);

  const selectedTags = Object.keys(discoverFilterParams)
    .reduce((acc: TagLabel[], key: string) => {
      // @ts-ignore
      return acc.concat(discoverFilterParams[key as keyof DiscoverParams]);
    }, [])
    ?.filter((tag) => tag);

  const hasTags =
    selectedTags?.filter(
      (x) => x?.key?.length > 0 && x?.params !== SearchParams.SORT_DISCOVER,
    )?.length > 0 || selectedMetadataTags?.length > 0;

  const handleSelectOption = (value: string, currentOptions: string[]) => {
    if (value === ALL) {
      return ALL;
    }
    const indexOfSelectedOption = currentOptions?.findIndex((x) => x === value);
    const isOptionSelected = indexOfSelectedOption > -1;

    // If option is already selected, remove option. Else add it to the list.
    if (isOptionSelected) {
      currentOptions.splice(indexOfSelectedOption, 1);
    } else {
      currentOptions.push(value);
    }
    return currentOptions
      ?.filter((x: string) => x !== ALL && x?.length > 0)
      ?.join(",");
  };

  const deleteChip = (value: string, paramsValue: string) => {
    if (paramsValue?.includes(",")) {
      const arr = paramsValue.split(",");
      const index = arr?.findIndex((x) => x === value?.toLowerCase());
      if (index > -1) {
        arr.splice(index, 1);
      }
      const strArr = arr?.join(",");
      return strArr;
    } else if (value.toLowerCase() === paramsValue.toLowerCase()) {
      return "";
    } else {
      return paramsValue;
    }
  };

  const addCategoryQueryToSearch = (name: string, value: string) => {
    // When user select option from CategoryFilters or when they click on a Tag.
    if (name === SearchParams.SORT_DISCOVER) {
      searchParams.set(name, value);
    } else {
      const paramsValue = searchParams.get(name) || "";
      const newParamsValue = handleSelectOption(value, paramsValue?.split(","));
      if (newParamsValue === "") {
        searchParams.delete(name);
      } else {
        searchParams.set(name, newParamsValue);
      }
    }
    setSearchParams(searchParams);
    storeUserQuery();
  };

  /**
   * When "All" is selected for a top-level category but one of the subcategories is deselected, deselect "All" and remove that one subcategory
   */
  const removeCategoryIdWithAllSelected = (
    name: string,
    deselectSubcategoryId: number,
    topLevelCategoryId: number,
  ) => {
    const currentValue = searchParams.get(name) || "";
    let currentOptions = currentValue.split(",");
    const subcategoryIds =
      categoriesById[topLevelCategoryId].subcategories?.map(
        (sub) => `${sub.id}`,
      ) || [];

    const indexOfSelectedOption = currentOptions?.findIndex(
      (x) => x === `${topLevelCategoryId}:all`,
    );
    const isOptionSelected = indexOfSelectedOption > -1;
    if (isOptionSelected) {
      currentOptions.splice(indexOfSelectedOption, 1);
    }
    const newOptions = subcategoryIds?.filter(
      (x) => x !== `${deselectSubcategoryId}`,
    );
    currentOptions = [...currentOptions, ...(newOptions || [])];

    _storeAllAndUser(currentOptions, name);
  };

  /**
   * Add or remove "All" of a top-level category when the user toggles the "All" checkbox
   */
  const addAllCategoryIdQueryToSearch = (
    name: string,
    topLevelCategoryId: number,
  ) => {
    const currentValue = searchParams.get(name) || "";
    let currentOptions = currentValue.split(",");
    const subcategoryIds =
      categoriesById[topLevelCategoryId].subcategories?.map(
        (sub) => `${sub.id}`,
      ) || [];

    const indexOfSelectedOption = currentOptions?.findIndex(
      (x) => x === `${topLevelCategoryId}:all` || x === `${topLevelCategoryId}`,
    );
    const isOptionSelected = indexOfSelectedOption > -1;

    if (isOptionSelected) {
      currentOptions.splice(indexOfSelectedOption, 1);
    } else {
      currentOptions.push(`${topLevelCategoryId}:all`);
      currentOptions = currentOptions?.filter(
        (x) => !subcategoryIds.includes(x),
      );
    }
    _storeAllAndUser(currentOptions, name);
  };

  const _storeAllAndUser = (currentOptions: string[], name: string) => {
    const joined = currentOptions
      ?.filter((x: string) => x?.length > 0)
      ?.join(",");
    searchParams.set(name, joined);
    setSearchParams(searchParams);
    storeUserQuery();
  };

  const clearAll = () => {
    searchParams.delete(SearchParams.TAGS_DISCOVER);
    searchParams.delete(SearchParams.CATEGORY_ID_DISCOVER);
    searchParams.delete(SearchParams.TYPE_DISCOVER);
    searchParams.delete(SearchParams.CONTACT_DISCOVER);
    searchParams.delete(SearchParams.LOCATION_DISCOVER);
    searchParams.delete(SearchParams.GEOPOINTS_DISCOVER);
    searchParams.delete(SearchParams.FOLLOWING_DISCOVER);
    searchParams.delete(SearchParams.AGE_DISCOVER);
    searchParams.delete(SearchParams.GENDER_DISCOVER);
    searchParams.delete(SearchParams.COMPENSATION_DISCOVER);
    setSearchParams(searchParams);
    storeUserQuery();
  };

  const deleteCategoryFromSearch = (chip: TagLabel) => {
    // Delete a tag from Category dropdown.
    if (chip.params === SearchParams.GEOPOINTS_DISCOVER && chip.key) {
      const parsed = JSON.parse(chip.key);
      if (parsed?.longitude && parsed?.latitude) {
        handleDeleteGeopoint(parsed.latitude, parsed.longitude);
        return;
      }
    }

    const paramsValue = searchParams.get(chip.params) || "";
    const strArr = deleteChip(chip.key, paramsValue);
    if (strArr === "") {
      searchParams.delete(chip.params);
    } else {
      searchParams.set(chip.params, strArr);
    }
    setSearchParams(searchParams);
    storeUserQuery();
  };

  const deleteMetadataFromSearch = (tag: BentoBrandMetadataTags) => {
    // Delete a tag from Metadata.
    setSelectedMetadataTags((prev) => {
      const newArray = prev?.filter((x) => x.value !== tag.value);
      sessionStorage.setItem(DISCOVER_METADATA_TAGS, JSON.stringify(newArray));
      return newArray;
    });
    if (tag.type === MetadataType.location && tag.latitude && tag.longitude) {
      handleDeleteGeopoint(tag.latitude, tag.longitude);
    } else {
      const params = MetadataTypeToParams[tag.type];
      const strArr = deleteChip(tag.value, searchParams.get(params) || "");
      if (strArr) {
        searchParams.set(params, strArr);
      } else {
        searchParams.delete(params);
      }
      setSearchParams(searchParams);
      storeUserQuery();
    }
  };

  const handleAddGeopoint = (geopoint: DiscoverSearchGeopoint) => {
    const { country_code, latitude, longitude, label, is_country } = geopoint;

    // Search location using latitude / longitude.
    const value: DiscoverSearchGeopoint = {
      country_code,
      latitude,
      longitude,
      label,
      is_country,
    };
    let arrayOfGeopoints = [];
    const paramsValue = searchParams.get(SearchParams.GEOPOINTS_DISCOVER) || "";
    if (paramsValue) {
      arrayOfGeopoints = JSON.parse(paramsValue);
    }
    const exists = arrayOfGeopoints?.find(
      (x: DiscoverSearchGeopoint) =>
        (x.latitude === latitude && x.longitude === longitude) ||
        x.label === label,
    );

    if (!exists) {
      arrayOfGeopoints.push(value);
    }

    if (arrayOfGeopoints?.length > 0) {
      searchParams.set(
        SearchParams.GEOPOINTS_DISCOVER,
        JSON.stringify(arrayOfGeopoints),
      );
    } else {
      searchParams.delete(SearchParams.GEOPOINTS_DISCOVER);
    }
    setSearchParams(searchParams);
    storeUserQuery();
  };

  const handleDeleteGeopoint = (latitude: number, longitude: number) => {
    // Remove latitude / longitude from search.
    const paramsValue = searchParams.get(SearchParams.GEOPOINTS_DISCOVER);
    if (!paramsValue) return;
    const arrayOfGeopoints = JSON.parse(paramsValue);
    const index = arrayOfGeopoints?.findIndex(
      (x: DiscoverSearchGeopoint) =>
        x.latitude === latitude && x.longitude === longitude,
    );
    if (index > -1) {
      arrayOfGeopoints.splice(index, 1);
    }
    if (arrayOfGeopoints?.length === 0) {
      searchParams.delete(SearchParams.GEOPOINTS_DISCOVER);
    } else {
      const strArr = JSON.stringify(arrayOfGeopoints);
      searchParams.set(SearchParams.GEOPOINTS_DISCOVER, strArr);
    }
    setSearchParams(searchParams);
    storeUserQuery();
  };

  const handleClickOnTag = (
    e: CustomEvent,
    tag: BentoBrandMetadataTags,
    setInputValue?: Dispatch<SetStateAction<string>>,
  ) => {
    trackEvent("Brand Card - Tag Clicked", { Tag: tag.value });
    e.stopPropagation();
    searchParams.delete(SearchParams.QUERY_DISCOVER);
    setSearchParams(searchParams);

    if (tag?.type === MetadataType.location && tag?.value) {
      if (
        tag?.locationType === MetadataLocationType.continent ||
        tag?.locationType === MetadataLocationType?.country
      ) {
        const categoryLocation = LOCATIONS?.find(
          (x) => x.name === lodash.startCase(tag?.value),
        );
        if (categoryLocation) {
          addCategoryQueryToSearch(
            SearchParams.LOCATION_DISCOVER,
            categoryLocation.key,
          );
          return;
        }
      }
      if (setInputValue) setInputValue(tag?.value);
    } else {
      const params = MetadataTypeToParams[tag.type];
      const searchQuery = searchParams.get(params);
      let arr = searchQuery?.split(",");
      if (!arr) {
        arr = [];
      }
      let value: any = tag?.value;
      const category = Object.values(categoriesById).find(
        (c) => c.name.toLowerCase() === value.toLowerCase(),
      );
      if (category) {
        addCategoryQueryToSearch(
          SearchParams.CATEGORY_ID_DISCOVER,
          `${category.id}`,
        );
        return;
      }

      const exists = arr?.find((x) => x === value.toLowerCase());
      if (!exists) {
        arr.push(value?.toLowerCase());
        setSelectedMetadataTags((prev) => {
          const exists = prev?.find((x) => x.value?.toLowerCase() === value);
          if (!exists) {
            prev.push(tag);
          }
          sessionStorage.setItem(DISCOVER_METADATA_TAGS, JSON.stringify(prev));
          return prev;
        });
      }
      const newStr = arr?.join(",");
      searchParams.set(params, newStr);
      setSearchParams(searchParams);
      storeUserQuery();
    }
  };

  const storeUserQuery = () => {
    // Store user's search query.
    const urlWithQuery = window.location.href?.split("?");

    let relevantKey = false;
    if (urlWithQuery?.length >= 2) {
      searchParams.forEach((value, key) => {
        if (
          key in DISCOVER_FILTER ||
          key === SearchParams.CATEGORY_ID_DISCOVER ||
          key === SearchParams.TAGS_DISCOVER ||
          key === SearchParams.GEOPOINTS_DISCOVER
        ) {
          relevantKey = true;
        }
      });
      if (relevantKey) {
        const query = urlWithQuery[1];
        sessionStorage.setItem(DISCOVER_SEARCH_PARAMS, query);
      } else {
        sessionStorage.removeItem(DISCOVER_SEARCH_PARAMS);
      }
    } else {
      sessionStorage.removeItem(DISCOVER_SEARCH_PARAMS);
    }
  };

  const handleRequestForBrand = async (
    requestFor: string,
    source: UserRequestSource,
  ) => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/user-requests`,
        "POST",
        {},
        {
          description: requestFor,
          source,
        },
      );
      if (source === UserRequestSource.SOURCE_REQUEST_BRAND) {
        const combinedRequest: CombinedRequest = {
          userRequestStatus: RequestStatus.pending,
          userRequestUpdatedAt: res.userRequest.updatedAt,
          bentoBrandIdOrUserRequestDescription: res.userRequest.description,
        };
        setUserRequests(SavedBrandStatus.unsent)((prev) => [
          ...prev,
          combinedRequest,
        ]);
        setTotal((prev) => ({
          ...prev,
          unsent: (prev.unsent || 0) + 1,
        }));
        decreaseBrandRequestsCount();
      }
      setAlert("Request submitted successfully!", "success");
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const submitBentoSearchParams = (
    searchParameters: BentoBrandSearchParameters,
  ) => {
    const searchParams = new URLSearchParams();

    if (searchParameters.query) {
      searchParams.set(SearchParams.QUERY_DISCOVER, searchParameters.query);
    }

    if (searchParameters.sort) {
      searchParams.set(SearchParams.SORT_DISCOVER, searchParameters.sort);
    }

    if (searchParameters.category_ids?.length) {
      searchParams.set(
        SearchParams.CATEGORY_ID_DISCOVER,
        searchParameters.category_ids.join(","),
      );
    }

    if (searchParameters.company_types?.length) {
      searchParams.set(
        SearchParams.TYPE_DISCOVER,
        searchParameters.company_types.join(","),
      );
    }

    if (searchParameters.contact_types?.length) {
      searchParams.set(
        SearchParams.CONTACT_DISCOVER,
        searchParameters.contact_types.join(","),
      );
    }

    if (searchParameters.locations?.length) {
      searchParams.set(
        SearchParams.LOCATION_DISCOVER,
        searchParameters.locations.join(","),
      );
    }

    if (searchParameters.geopoints?.length) {
      searchParams.set(
        SearchParams.GEOPOINTS_DISCOVER,
        JSON.stringify(searchParameters.geopoints.map((g) => JSON.parse(g))),
      );
    }

    const minIgFollowers = searchParameters.min_instagram_followers ?? 0;
    const maxIgFollowers = searchParameters.max_instagram_followers;

    if (minIgFollowers !== undefined && maxIgFollowers !== undefined) {
      let followersSize = [];
      for (const key in FollowingSizeRange) {
        if (
          FollowingSizeRange[key]?.min >= minIgFollowers &&
          FollowingSizeRange[key]?.max <= maxIgFollowers
        ) {
          followersSize.push(key);
        }
      }
      if (followersSize?.length > 0) {
        searchParams.set(
          SearchParams.FOLLOWING_DISCOVER,
          followersSize.join(","),
        );
      }
    }

    if (searchParameters.compensation_types?.length) {
      searchParams.set(
        SearchParams.COMPENSATION_DISCOVER,
        searchParameters.compensation_types.join(","),
      );
    }

    if (searchParameters.target_genders?.length) {
      searchParams.set(
        SearchParams.GENDER_DISCOVER,
        searchParameters.target_genders.join(","),
      );
    }

    if (
      !(
        searchParameters.min_target_age === undefined &&
        searchParameters.max_target_age === undefined
      ) &&
      !(
        (searchParameters.min_target_age ?? 0) === 0 &&
        (searchParameters.max_target_age ?? 150) === 150
      )
    ) {
      const ageKeys = Object.entries(TargetAgeRange)
        .filter(
          ([, range]) =>
            !(
              range.max < (searchParameters.min_target_age ?? 0) ||
              range.min > (searchParameters.max_target_age ?? 150)
            ),
        )
        .map(([key]) => key);

      if (ageKeys.length > 0) {
        searchParams.set(SearchParams.AGE_DISCOVER, ageKeys.join(","));
      }
    }

    setSearchParams(searchParams);
    storeUserQuery();
  };

  return {
    setSearchParams,
    storeUserQuery,
    handleAddGeopoint,
    handleDeleteGeopoint,
    addCategoryQueryToSearch,
    deleteCategoryFromSearch,
    deleteMetadataFromSearch,
    handleClickOnTag,
    addAllCategoryIdQueryToSearch,
    handleRequestForBrand,
    hasTags,
    removeCategoryIdWithAllSelected,
    selectedTags,
    submitBentoSearchParams,
    clearAll,
  };
};
